/**
 * FolderFinder — reusable Google Drive folder picker modal.
 *
 * Pass 1 capabilities (this file):
 *   ✅ Search by name (Drive `name contains`)
 *   ✅ Browse the folder tree (lazy-loaded, like the sidebar)
 *   ✅ Recent folders (per-user, last 20)
 *   ✅ Path breadcrumbs in the preview pane
 *   ✅ Create a new folder anywhere in Browse
 *   ✅ "Open in Drive" button on the selected folder
 *   ✅ Friendly error states (not connected, permission denied, etc.)
 *
 * Pass 2 (deferred):
 *   ⏳ Shared Drives, Starred, Shared with me
 *   ⏳ Generalized linking to Customer/Property/Estimate/WorkOrder
 *   ⏳ Reorganize-existing-folders wizard
 *
 * Scope reminder: our OAuth scope is `drive.file`, so this only sees
 * folders the app has access to (created or shared with the app).
 * The "Add by URL" affordance lets a user paste a folder URL — if the
 * folder is accessible via files.get, it's added; otherwise we surface
 * a clear error.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiClient } from '@/api/client';
import { toast } from 'sonner';
import {
  ChevronDown, ChevronRight, Clock, Cloud, ExternalLink, Folder, FolderPlus,
  Loader2, Plus, Search, X, AlertTriangle, Link2, Star, Users, Building, Link as LinkIcon,
} from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { getActiveCompanyTz } from '@/lib/companyTime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROOT = '__root__';

/**
 * @param open boolean
 * @param onOpenChange (boolean) => void
 * @param title string
 * @param cta string                "Use this folder" by default
 * @param initialSelectedId string?  Pre-select on open
 * @param excludeId string?          Hide this folder from results (for "move folder" — can't move into self)
 * @param allowRoot boolean          Allow selecting "My Drive root"
 * @param recordRecent boolean       POST to /recent-folders on confirm (default true)
 * @param linkContext object?        Optional {entity_type, entity_id, entity_label}.
 *                                   When provided, the preview pane shows
 *                                   a "Link to {entity_label}" button that
 *                                   POSTs to /folder-links AFTER the user
 *                                   confirms the picker (in addition to
 *                                   onConfirm). Useful for Customer/Job
 *                                   pages.
 * @param onConfirm (folderId|null, folderName, fullPath) => void
 */
export default function FolderFinder({
  open, onOpenChange,
  title = 'Pick a Google Drive folder',
  cta = 'Use this folder',
  initialSelectedId = null,
  // Optional starting tab — useful for surfaces that should bias the
  // user toward a specific scope (e.g., "Choose a Shared Drive folder
  // for company storage" pre-selects the Shared Drives tab).
  initialTab = 'browse',
  excludeId = null,
  allowRoot = true,
  recordRecent = true,
  linkContext = null,
  onConfirm,
}) {
  const [tab, setTab] = useState(initialTab);      // 'browse' | 'recent' | 'search' | 'starred' | 'shared_with_me' | 'shared_drives'
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState(null);  // null = not searched yet
  const [searchBusy, setSearchBusy] = useState(false);
  const [recents, setRecents] = useState([]);
  const [recentsLoading, setRecentsLoading] = useState(false);

  // Pass 2 tab data — lazy-loaded when the tab is first activated.
  const [starred, setStarred] = useState(null);
  const [sharedWithMe, setSharedWithMe] = useState(null);
  const [sharedDrives, setSharedDrives] = useState(null);

  // Paste-by-URL state.
  const [urlInput, setUrlInput] = useState('');
  const [urlBusy, setUrlBusy] = useState(false);

  // Existing folder-links for the selected folder.
  const [folderLinks, setFolderLinks] = useState([]);
  const [linkBusy, setLinkBusy] = useState(false);

  // Browse-tree state (flat node map + root list, like DriveFolderTree).
  const [nodes, setNodes] = useState({});
  const [rootIds, setRootIds] = useState(null);
  const [browseError, setBrowseError] = useState(null);
  const [creatingUnder, setCreatingUnder] = useState(null);
  const [createValue, setCreateValue] = useState('');
  // Toolbar "+ New folder" affordance — independent of the per-row
  // create flow. Opens a small inline input above the folder list.
  const [creatingTopLevel, setCreatingTopLevel] = useState(false);
  const [topLevelName, setTopLevelName] = useState('');

  // Selected folder + its details/path (right pane).
  const [selectedId, setSelectedId] = useState(initialSelectedId);
  const [selectedName, setSelectedName] = useState('');
  const [details, setDetails] = useState(null);
  const [pathBreadcrumbs, setPathBreadcrumbs] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // ── Reset state on every open so this is a fresh picker each time ──
  useEffect(() => {
    if (!open) return;
    setTab(initialTab);
    setSearch('');
    setSearchResults(null);
    setNodes({});
    setRootIds(null);
    setBrowseError(null);
    setCreatingUnder(null);
    setCreateValue('');
    setSelectedId(initialSelectedId);
    setSelectedName('');
    setDetails(null);
    setPathBreadcrumbs(null);
    setStarred(null);
    setSharedWithMe(null);
    setSharedDrives(null);
    setUrlInput('');
    setFolderLinks([]);
    // `initialTab` is intentionally read once when the picker opens — changes
    // to it mid-session shouldn't reset the user's tab choice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialSelectedId]);

  // ── Fetch root level + recents on open ─────────────────────────────
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setRecentsLoading(true);
    apiClient.get('/integrations/google-drive/recent-folders')
      .then(({ data }) => { if (!cancelled) setRecents(data || []); })
      .catch(() => { if (!cancelled) setRecents([]); })
      .finally(() => { if (!cancelled) setRecentsLoading(false); });

    apiClient.get('/integrations/google-drive/folders?parent=root')
      .then(({ data }) => {
        if (cancelled) return;
        const folders = (data.folders || []).filter(f => f.id !== excludeId);
        const next = {};
        for (const f of folders) {
          next[f.id] = { folder: f, depth: 0, parentId: null, expanded: false, childIds: null };
        }
        setNodes(next);
        setRootIds(folders.map(f => f.id));
      })
      .catch(err => {
        if (cancelled) return;
        const status = err.response?.status;
        if (status === 400) setBrowseError('not_connected');
        else if (status === 403) setBrowseError('permission_denied');
        else setBrowseError('load_failed');
        setRootIds([]);
      });
    return () => { cancelled = true; };
  }, [open, excludeId]);

  // ── Debounced search ───────────────────────────────────────────────
  const searchTimer = useRef(null);
  useEffect(() => {
    if (!open) return;
    if (!search.trim()) {
      setSearchResults(null);
      // If user clears search, switch back to whichever tab they were on.
      if (tab === 'search') setTab('browse');
      return;
    }
    setTab('search');
    setSearchBusy(true);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      try {
        const { data } = await apiClient.get('/integrations/google-drive/folders/search', { params: { q: search.trim(), limit: 50 } });
        setSearchResults((data.folders || []).filter(f => f.id !== excludeId));
      } catch (err) {
        const status = err.response?.status;
        if (status === 400) setBrowseError('not_connected');
        setSearchResults([]);
      } finally {
        setSearchBusy(false);
      }
    }, 300);
    return () => clearTimeout(searchTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, open, excludeId]);

  // ── Lazy-load the Pass 2 tabs when first activated ─────────────────
  useEffect(() => {
    if (!open) return;
    if (tab === 'starred' && starred === null) {
      apiClient.get('/integrations/google-drive/starred-folders')
        .then(({ data }) => setStarred(data.folders || []))
        .catch(() => setStarred([]));
    }
    if (tab === 'shared_with_me' && sharedWithMe === null) {
      apiClient.get('/integrations/google-drive/shared-with-me-folders')
        .then(({ data }) => setSharedWithMe(data.folders || []))
        .catch(() => setSharedWithMe([]));
    }
    if (tab === 'shared_drives' && sharedDrives === null) {
      apiClient.get('/integrations/google-drive/shared-drives')
        .then(({ data }) => {
          const drives = data.drives || [];
          setSharedDrives(drives);
          // Seed the nodes map so each Shared Drive can be expanded into
          // its subfolder tree using the same BrowseTree machinery as
          // My Drive. Each Shared Drive acts as a depth-0 root with no
          // parent; subfolders are lazily loaded via toggleExpand.
          setNodes(prev => {
            const next = { ...prev };
            for (const d of drives) {
              if (!next[d.id]) {
                next[d.id] = {
                  folder: { id: d.id, name: d.name, parent_id: null, is_shared_drive: true },
                  depth: 0,
                  parentId: null,
                  expanded: false,
                  childIds: null,
                };
              }
            }
            return next;
          });
        })
        .catch(() => setSharedDrives([]));
    }
  }, [tab, open, starred, sharedWithMe, sharedDrives]);

  // Root ids for the Shared Drives tab — each Shared Drive becomes its
  // own expandable root in the tree view (mirrors how My Drive's "root"
  // is expandable in the Browse tab).
  const sharedDriveRootIds = useMemo(
    () => (sharedDrives || []).map(d => d.id),
    [sharedDrives],
  );
  // Visible rows for the Shared Drives tab — same stack-walk algorithm
  // as `visibleRows` for Browse, but rooted at Shared Drive ids.
  const visibleRowsSD = useMemo(() => {
    if (!sharedDriveRootIds.length) return [];
    const out = [];
    const stack = [...sharedDriveRootIds].reverse();
    while (stack.length) {
      const id = stack.pop();
      const node = nodes[id];
      if (!node) continue;
      out.push(node);
      if (node.expanded && node.childIds && node.childIds.length) {
        for (let i = node.childIds.length - 1; i >= 0; i--) stack.push(node.childIds[i]);
      }
    }
    return out;
  }, [nodes, sharedDriveRootIds]);

  // ── Fetch existing links for the selected folder ───────────────────
  useEffect(() => {
    if (!selectedId || selectedId === ROOT) { setFolderLinks([]); return; }
    let cancelled = false;
    apiClient.get('/integrations/google-drive/folder-links', {
        params: { drive_folder_id: selectedId },
        })
      .then(({ data }) => { if (!cancelled) setFolderLinks(data || []); })
      .catch(() => { if (!cancelled) setFolderLinks([]); });
    return () => { cancelled = true; };
  }, [selectedId]);

  // ── Paste-by-URL: extract ID, fetch details, select it ─────────────
  const handlePasteUrl = useCallback(async () => {
    const url = urlInput.trim();
    if (!url) return;
    setUrlBusy(true);
    try {
      const { data } = await apiClient.post('/integrations/google-drive/folders/by-url', { url });
      // Found! Push it into Recent and select it.
      setSelectedId(data.id);
      setSelectedName(data.name || '');
      setDetails(data);
      // Resolve path so breadcrumbs render.
      try {
        const { data: pathRes } = await apiClient.get(`/integrations/google-drive/folders/${data.id}/path`);
        setPathBreadcrumbs(pathRes.path || []);
      } catch { /* non-fatal */ }
      setUrlInput('');
      toast.success(`Found "${data.name}"`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not resolve that URL');
    } finally {
      setUrlBusy(false);
    }
  }, [urlInput]);


  const toggleExpand = useCallback(async (id) => {
    const current = nodes[id];
    if (!current) return;
    if (current.childIds === null) {
      try {
        const { data } = await apiClient.get(`/integrations/google-drive/folders?parent=${id}`);
        const folders = (data.folders || []).filter(f => f.id !== excludeId);
        setNodes(prev => {
          const next = { ...prev };
          for (const f of folders) {
            next[f.id] = next[f.id] || { folder: f, depth: (prev[id]?.depth ?? 0) + 1, parentId: id, expanded: false, childIds: null };
          }
          next[id] = { ...prev[id], expanded: true, childIds: folders.map(f => f.id) };
          return next;
        });
      } catch {
        setNodes(prev => ({ ...prev, [id]: { ...prev[id], expanded: true, childIds: [] } }));
      }
    } else {
      setNodes(prev => ({ ...prev, [id]: { ...prev[id], expanded: !prev[id].expanded } }));
    }
  }, [nodes, excludeId]);

  // ── Visible row list for Browse mode (stack walk) ──────────────────
  const visibleRows = useMemo(() => {
    if (!rootIds) return [];
    const out = [];
    const stack = [...rootIds].reverse();
    while (stack.length) {
      const id = stack.pop();
      const node = nodes[id];
      if (!node) continue;
      out.push(node);
      if (node.expanded && node.childIds && node.childIds.length) {
        for (let i = node.childIds.length - 1; i >= 0; i--) stack.push(node.childIds[i]);
      }
    }
    return out;
  }, [nodes, rootIds]);

  // ── Selection: fetch details + path ────────────────────────────────
  const handleSelect = useCallback(async (id, fallbackName) => {
    setSelectedId(id);
    setSelectedName(fallbackName || '');
    setDetails(null);
    setPathBreadcrumbs(null);
    if (id === ROOT || id === null) {
      setPathBreadcrumbs([{ id: null, name: 'My Drive' }]);
      return;
    }
    setDetailsLoading(true);
    try {
      const [detailsRes, pathRes] = await Promise.all([
        apiClient.get(`/integrations/google-drive/folders/${id}/details`),
        apiClient.get(`/integrations/google-drive/folders/${id}/path`),
      ]);
      setDetails(detailsRes.data);
      setPathBreadcrumbs(pathRes.data.path || []);
      if (detailsRes.data?.name) setSelectedName(detailsRes.data.name);
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) toast.error('Folder not accessible (it may be deleted or out of your Drive scope).');
      else toast.error(err.response?.data?.detail || 'Failed to load folder details');
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  // ── Inline create folder ──────────────────────────────────────────
  const handleCreate = useCallback(async (parentId) => {
    const name = createValue.trim();
    if (!name) return;
    try {
      const { data } = await apiClient.post('/integrations/google-drive/folders', { name, parent_id: parentId === ROOT ? null : parentId });
      toast.success(`Created "${data.name}"`);
      // Inject into tree.
      setNodes(prev => {
        if (parentId === ROOT) {
          return {
            ...prev,
            [data.id]: {
              folder: { id: data.id, name: data.name, parent_id: data.parent_id },
              depth: 0, parentId: null, expanded: false, childIds: null,
            },
          };
        }
        const parentNode = prev[parentId];
        if (!parentNode) return prev;
        return {
          ...prev,
          [data.id]: {
            folder: { id: data.id, name: data.name, parent_id: data.parent_id },
            depth: parentNode.depth + 1, parentId, expanded: false, childIds: null,
          },
          [parentId]: {
            ...parentNode,
            expanded: true,
            childIds: [data.id, ...(parentNode.childIds || [])],
          },
        };
      });
      if (parentId === ROOT) {
        setRootIds(prev => [data.id, ...(prev || [])]);
      }
      handleSelect(data.id, data.name);
      setCreatingUnder(null);
      setCreateValue('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Create failed');
    }
  }, [createValue, handleSelect]);

  /**
   * Toolbar "+ New folder" — creates inside whatever's currently
   * selected. Falls back to whichever scope makes sense for the
   * active tab when nothing is selected:
   *   - Browse / search / recent / starred / shared_with_me → My Drive root
   *   - Shared Drives → require selection (can't create at top level
   *     of "Shared Drives" since that's a virtual list of drives)
   * Switches to Browse tab post-create so the user can see their new
   * folder in context.
   */
  const handleTopLevelCreate = useCallback(async () => {
    const name = topLevelName.trim();
    if (!name) return;
    let parent = null;
    if (selectedId && selectedId !== ROOT) parent = selectedId;
    else if (tab === 'shared_drives') {
      toast.error('Pick a Shared Drive first', {
        description: 'New folders need a parent — select a Shared Drive then click + New folder.',
      });
      return;
    }
    try {
      const { data } = await apiClient.post('/integrations/google-drive/folders', { name, parent_id: parent });
      toast.success(`Created "${data.name}"`);
      // Cache the node so the picker shows it in tree views.
      setNodes(prev => ({
        ...prev,
        [data.id]: {
          folder: { id: data.id, name: data.name, parent_id: data.parent_id },
          depth: 0, parentId: parent, expanded: false, childIds: null,
        },
      }));
      handleSelect(data.id, data.name);
      setCreatingTopLevel(false);
      setTopLevelName('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Create failed');
    }
  }, [topLevelName, selectedId, tab, handleSelect]);

  // ── Confirm selection ────────────────────────────────────────────
  const handleConfirm = useCallback(async () => {
    if (!selectedId) return;
    const breadcrumbs = pathBreadcrumbs || [];
    const fullPath = breadcrumbs.map(b => b.name).join(' > ');
    if (recordRecent && selectedId !== ROOT) {
      // Fire-and-forget; the picker doesn't block on this.
      apiClient.post('/integrations/google-drive/recent-folders', { folder_id: selectedId, folder_name: selectedName, folder_path: fullPath }).catch(() => { /* non-fatal */ });
    }
    // Surface the Shared Drive context (if any) to the parent so use
    // cases that REQUIRE Shared-Drive backing (e.g., company storage
    // root selection) can validate it. We resolve the Shared Drive
    // id/name from either:
    //   1) details.driveId / details.driveName (Drive API response), or
    //   2) the user picking directly from the Shared Drives tab — in
    //      which case selectedId IS the Shared Drive id and we look up
    //      the matching name from the cached drives list.
    let sharedDriveCtx = null;
    if (details && details.driveId) {
      sharedDriveCtx = { id: details.driveId, name: details.driveName || '' };
    } else if (sharedDrives) {
      const direct = (sharedDrives || []).find(d => d.id === selectedId);
      if (direct) sharedDriveCtx = { id: direct.id, name: direct.name };
    }
    onConfirm(
      selectedId === ROOT ? null : selectedId,
      selectedName,
      fullPath,
      { sharedDrive: sharedDriveCtx, details, breadcrumbs },
    );
  }, [selectedId, selectedName, pathBreadcrumbs, onConfirm, recordRecent, details, sharedDrives]);

  // ── Browse error overlay ────────────────────────────────────────
  const errorPane = browseError && (
    <div data-testid="folder-finder-error" className="flex flex-col items-center justify-center py-10 px-6 text-center">
      <AlertTriangle className="w-7 h-7 text-[#C4A561] mb-2" />
      {browseError === 'not_connected' && (
        <>
          <p className="text-sm text-foreground">Google Drive isn't connected</p>
          <p className="text-xs text-muted-foreground mt-1">Connect Drive in Settings → Connections to use the folder finder.</p>
          <a href="/settings" className="mt-3 text-xs text-[#4575A8] hover:underline">Open Settings</a>
        </>
      )}
      {browseError === 'permission_denied' && (
        <>
          <p className="text-sm text-foreground">Permission denied</p>
          <p className="text-xs text-muted-foreground mt-1">Reconnect Google Drive to grant the required scopes.</p>
        </>
      )}
      {browseError === 'load_failed' && (
        <>
          <p className="text-sm text-foreground">Couldn't load Drive folders</p>
          <p className="text-xs text-muted-foreground mt-1">Try again in a moment.</p>
        </>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="folder-finder"
        className="max-w-4xl bg-card border-border text-foreground p-0 overflow-hidden"
        // Prevent dropdown menus, popovers, and other modal overlays
        // from auto-dismissing the picker. This matches the behaviour of
        // Google's own Drive Picker (full-screen, modal until user
        // explicitly cancels or selects).
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        {/* ── Header (Google-Picker style): title + horizontal tabs ── */}
        <div className="px-6 pt-5 pb-0 border-b border-border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-medium text-foreground" style={{ fontFamily: 'Outfit' }}>
              {title}
            </h3>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="text-muted-foreground hover:text-foreground -mr-1 p-1"
              aria-label="Close"
              data-testid="folder-finder-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {/* Horizontal tab strip — mirrors Google Drive Picker. */}
          <div className="flex items-center gap-0 -mb-px overflow-x-auto" data-testid="folder-finder-tabs">
            <TopTab icon={Folder} label="My Drive" active={tab === 'browse' || tab === 'search'} onClick={() => { setTab('browse'); }} testId="folder-finder-tab-browse" />
            <TopTab icon={Building} label="Shared drives" active={tab === 'shared_drives'} onClick={() => { setTab('shared_drives'); setSearch(''); }} testId="folder-finder-tab-shared-drives" />
            <TopTab icon={Users} label="Shared with me" active={tab === 'shared_with_me'} onClick={() => { setTab('shared_with_me'); setSearch(''); }} testId="folder-finder-tab-shared-with-me" />
            <TopTab icon={Star} label="Starred" active={tab === 'starred'} onClick={() => { setTab('starred'); setSearch(''); }} testId="folder-finder-tab-starred" />
            <TopTab icon={Clock} label={`Recent${recents.length ? ` (${recents.length})` : ''}`} active={tab === 'recent'} onClick={() => { setTab('recent'); setSearch(''); }} testId="folder-finder-tab-recent" />
          </div>
        </div>
        {/* Toolbar — search + paste-by-URL escape hatch. */}
        <div className="px-6 py-3 bg-background border-b border-border flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="folder-finder-search"
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search Drive folders by name…"
              className="bg-card border-border text-foreground pl-9 pr-7 h-8 text-xs"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
          <div className="relative w-72">
            <LinkIcon className="w-3 h-3 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="folder-finder-url-input"
              value={urlInput}
              onChange={e => setUrlInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handlePasteUrl(); } }}
              placeholder="…or paste a Drive folder URL / ID"
              className="bg-card border-border text-muted-foreground pl-8 pr-16 h-8 text-[11px]"
            />
            <Button
              data-testid="folder-finder-url-go"
              size="sm"
              variant="outline"
              disabled={!urlInput.trim() || urlBusy}
              onClick={handlePasteUrl}
              className="absolute right-1 top-1/2 -translate-y-1/2 h-6 px-2 text-[10px] border-border text-muted-foreground"
            >
              {urlBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Resolve'}
            </Button>
          </div>
          {/* Global "+ New folder" — creates inside the currently selected
              folder (or at the root of My Drive if nothing selected).
              Mirrors Google's own picker which always exposes folder
              creation at the toolbar level rather than per-row. */}
          <Button
            type="button"
            data-testid="folder-finder-new-folder"
            size="sm"
            variant="outline"
            disabled={creatingTopLevel}
            onClick={() => setCreatingTopLevel(true)}
            className="h-8 px-2.5 text-[11px] border-border text-muted-foreground hover:text-foreground flex-shrink-0"
            title={selectedId ? `Create new folder inside "${selectedName}"` : 'Create new folder in My Drive'}
          >
            <Plus className="w-3 h-3 mr-1" />
            New folder
          </Button>
        </div>

        {/* ── Body: main + (optional) right preview ─────────────── */}
        <div className="flex" style={{ height: '460px' }}>
          {/* Main column */}
          <div className="flex-1 overflow-y-auto" data-testid="folder-finder-main">
            {/* Inline "+ New folder" creator — driven by the toolbar
                button. Shows context (parent breadcrumb) so the admin
                knows EXACTLY where the new folder will be born. On the
                Shared Drives tab the user MUST pick a parent first
                because "Shared Drives" itself isn't a real folder. */}
            {creatingTopLevel && (() => {
              const onSharedTabNoParent = tab === 'shared_drives' && (!selectedId || selectedId === ROOT);
              const parentLabel = onSharedTabNoParent
                ? 'Select a Shared Drive below first'
                : (selectedId && selectedName ? selectedName : 'My Drive');
              return (
              <div data-testid="folder-finder-new-folder-inline" className="px-4 py-3 bg-[#4575A8]/5 border-b border-[#4575A8]/20">
                <div className="text-[10px] uppercase tracking-widest text-[#4575A8] mb-1.5 flex items-center gap-1.5">
                  <FolderPlus className="w-3 h-3" />
                  New folder in
                  <span className={`normal-case tracking-normal ${onSharedTabNoParent ? 'text-[#C4A561]' : 'text-foreground'}`}>
                    {parentLabel}
                  </span>
                </div>
                {onSharedTabNoParent ? (
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[11px] text-muted-foreground">
                      Click any Shared Drive in the list below to make it the parent, then come back here.
                    </p>
                    <Button
                      size="sm"
                      variant="ghost"
                      data-testid="folder-finder-new-folder-cancel"
                      onClick={() => { setCreatingTopLevel(false); setTopLevelName(''); }}
                      className="h-8 text-[11px] text-muted-foreground"
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Input
                      autoFocus
                      data-testid="folder-finder-new-folder-name"
                      value={topLevelName}
                      onChange={e => setTopLevelName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); handleTopLevelCreate(); }
                        if (e.key === 'Escape') { setCreatingTopLevel(false); setTopLevelName(''); }
                      }}
                      placeholder="Folder name…"
                      className="bg-card border-border text-foreground h-8 text-xs"
                    />
                    <Button
                      size="sm"
                      data-testid="folder-finder-new-folder-create"
                      onClick={handleTopLevelCreate}
                      disabled={!topLevelName.trim()}
                      className="h-8 text-[11px] bg-[#4575A8] hover:bg-[#5C8AC2] text-white"
                    >
                      Create
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      data-testid="folder-finder-new-folder-cancel"
                      onClick={() => { setCreatingTopLevel(false); setTopLevelName(''); }}
                      className="h-8 text-[11px] text-muted-foreground"
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
              );
            })()}
            {browseError && tab !== 'recent' ? errorPane : null}

            {/* Search results */}
            {!browseError && tab === 'search' && (
              <SearchResultsList
                busy={searchBusy}
                query={search}
                results={searchResults}
                selectedId={selectedId}
                onSelect={handleSelect}
              />
            )}

            {/* Browse mode */}
            {!browseError && tab === 'browse' && (
              <BrowseTree
                rootIds={rootIds}
                visibleRows={visibleRows}
                allowRoot={allowRoot}
                selectedId={selectedId}
                creatingUnder={creatingUnder}
                createValue={createValue}
                setCreateValue={setCreateValue}
                onSelectRoot={() => handleSelect(ROOT, 'My Drive')}
                onToggleExpand={toggleExpand}
                onSelect={handleSelect}
                onStartCreate={(parentId) => { setCreatingUnder(parentId); setCreateValue(''); }}
                onSubmitCreate={handleCreate}
                onCancelCreate={() => setCreatingUnder(null)}
              />
            )}

            {/* Recents */}
            {tab === 'recent' && (
              <RecentList
                loading={recentsLoading}
                items={recents}
                selectedId={selectedId}
                onSelect={handleSelect}
              />
            )}

            {/* Starred */}
            {!browseError && tab === 'starred' && (
              <FlatFolderList
                loading={starred === null}
                items={starred || []}
                selectedId={selectedId}
                onSelect={handleSelect}
                emptyText="No starred folders this app can see."
                testIdPrefix="folder-finder-starred-row"
              />
            )}

            {/* Shared with me */}
            {!browseError && tab === 'shared_with_me' && (
              <FlatFolderList
                loading={sharedWithMe === null}
                items={sharedWithMe || []}
                selectedId={selectedId}
                onSelect={handleSelect}
                emptyText="No folders shared with this Drive account (visible to this app)."
                testIdPrefix="folder-finder-shared-row"
                showSharer
              />
            )}

            {/* Shared Drives — same expandable BrowseTree as My Drive,
                rooted at Shared Drive ids so users can drill into
                subfolders without leaving the picker. */}
            {!browseError && tab === 'shared_drives' && (
              sharedDrives === null ? (
                <div className="px-4 py-6 text-xs text-muted-foreground flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
                </div>
              ) : (sharedDrives || []).length === 0 ? (
                <div className="px-4 py-6 text-xs text-muted-foreground text-center">
                  No Shared Drives. Shared Drives are a Google Workspace feature — your account may not have any.
                </div>
              ) : (
                <BrowseTree
                  rootIds={sharedDriveRootIds}
                  visibleRows={visibleRowsSD}
                  allowRoot={false}        /* "My Drive" pseudo-root makes no sense in Shared-Drive context */
                  selectedId={selectedId}
                  creatingUnder={creatingUnder}
                  createValue={createValue}
                  setCreateValue={setCreateValue}
                  onSelectRoot={() => {}}  /* no-op */
                  onToggleExpand={toggleExpand}
                  onSelect={handleSelect}
                  onStartCreate={(parentId) => { setCreatingUnder(parentId); setCreateValue(''); }}
                  onSubmitCreate={handleCreate}
                  onCancelCreate={() => setCreatingUnder(null)}
                />
              )
            )}
          </div>

          {/* Right preview pane — only rendered when a linkContext is
              provided (e.g., link-folder-to-customer flow). Most pickers
              (destination, move, find) don't pass linkContext, so we
              hide the pane to match Google's clean picker layout. */}
          {linkContext && (
          <div className="w-72 flex-shrink-0 border-l border-border bg-background p-4 overflow-y-auto" data-testid="folder-finder-preview">
            {!selectedId ? (
              <div className="text-xs text-muted-foreground text-center pt-12">
                Pick a folder to see details here.
              </div>
            ) : (
              <SelectedFolderPreview
                folderId={selectedId}
                folderName={selectedName}
                details={details}
                pathBreadcrumbs={pathBreadcrumbs}
                loading={detailsLoading}
                folderLinks={folderLinks}
                linkContext={linkContext}
                linkBusy={linkBusy}
                onLinkToContext={async () => {
                  if (!linkContext || !selectedId) return;
                  setLinkBusy(true);
                  try {
                    const breadcrumbs = pathBreadcrumbs || [];
                    const fullPath = breadcrumbs.map(b => b.name).join(' > ');
                    const { data } = await apiClient.post('/integrations/google-drive/folder-links', {
                        entity_type: linkContext.entity_type,
                        entity_id: linkContext.entity_id,
                        drive_folder_id: selectedId,
                        drive_folder_name: selectedName,
                        drive_folder_path: fullPath,
                      });
                    setFolderLinks(prev => {
                      // Avoid double-display if a previous link existed.
                      if (prev.find(l => l.id === data.id)) return prev;
                      return [data, ...prev];
                    });
                    toast.success(`Linked to ${linkContext.entity_label || linkContext.entity_type}`);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Link failed');
                  } finally {
                    setLinkBusy(false);
                  }
                }}
                onUnlink={async (linkId) => {
                  setLinkBusy(true);
                  try {
                    await apiClient.delete(`/integrations/google-drive/folder-links/${linkId}`);
                    setFolderLinks(prev => prev.filter(l => l.id !== linkId));
                    toast.success('Link removed');
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Unlink failed');
                  } finally {
                    setLinkBusy(false);
                  }
                }}
              />
            )}
          </div>
          )}
        </div>

        {/* ── Footer ──────────────────────────────────────────── */}
        <div className="px-6 py-3 border-t border-border flex justify-between items-center bg-background">
          <div className="flex flex-col">
            <p className="text-[10px] text-muted-foreground">
              Visible: folders this app has access to (created or shared with us).
            </p>
            {selectedId && selectedName && (
              <p className="text-[11px] text-foreground mt-1 flex items-center gap-1.5">
                <Folder className="w-3 h-3 text-[#4575A8]" />
                <span className="font-medium">{selectedName}</span>
                {pathBreadcrumbs && pathBreadcrumbs.length > 1 && (
                  <span className="text-muted-foreground">
                    · in {pathBreadcrumbs.slice(0, -1).map(b => b.name).join(' / ')}
                  </span>
                )}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="folder-finder-cancel"
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              className="border-border text-muted-foreground hover:bg-muted/40"
            >
              Cancel
            </Button>
            <Button
              data-testid="folder-finder-confirm"
              size="sm"
              onClick={handleConfirm}
              disabled={!selectedId}
              className="bg-[#1A73E8] hover:bg-[#1666CC] text-white disabled:bg-[#1A73E8]/30 disabled:text-white/50"
            >
              {cta}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Sub-components (kept in same file to keep import surface tiny) ──

/**
 * Top-of-modal pill tab — Google-Drive-Picker-style horizontal tab.
 * Shows icon + label, with an underline accent when active.
 */
function TopTab({ icon: Icon, label, active, onClick, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`flex items-center gap-1.5 px-4 py-2.5 text-xs whitespace-nowrap transition-colors border-b-2 ${
        active
          ? 'border-[#4575A8] text-foreground font-medium'
          : 'border-transparent text-muted-foreground hover:text-foreground'
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}

function SearchResultsList({ busy, query, results, selectedId, onSelect }) {
  if (busy) {
    return (
      <div className="px-4 py-6 text-xs text-muted-foreground flex items-center gap-2">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching for "{query}"…
      </div>
    );
  }
  if (results === null) return null;
  if (results.length === 0) {
    return (
      <div className="px-4 py-6 text-xs text-muted-foreground text-center">
        No folders match "{query}". Folders not created by this app aren't searchable — try Browse, or create a new folder.
      </div>
    );
  }
  return (
    <div className="py-1" data-testid="folder-finder-search-results">
      {results.map(f => (
        <button
          key={f.id}
          data-testid={`folder-finder-search-row-${f.id}`}
          onClick={() => onSelect(f.id, f.name)}
          className={`flex items-center gap-2 w-full px-4 py-2 text-left transition-colors text-xs ${
            selectedId === f.id ? 'bg-[#4575A8]/15 text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          }`}
        >
          <Folder className="w-3.5 h-3.5 flex-shrink-0 text-[#4575A8]" />
          <div className="flex-1 min-w-0">
            <div className="truncate text-foreground">{f.name}</div>
            <div className="text-[10px] text-muted-foreground truncate">in {f.parent_name || 'My Drive'}</div>
          </div>
        </button>
      ))}
    </div>
  );
}

function BrowseTree({
  rootIds, visibleRows, allowRoot, selectedId,
  creatingUnder, createValue, setCreateValue,
  onSelectRoot, onToggleExpand, onSelect,
  onStartCreate, onSubmitCreate, onCancelCreate,
}) {
  if (rootIds === null) {
    return (
      <div className="px-4 py-6 text-xs text-muted-foreground flex items-center gap-2">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
      </div>
    );
  }
  return (
    <div className="py-1">
      {allowRoot && (
        <div
          data-testid="folder-finder-row-root"
          onClick={onSelectRoot}
          className={`flex items-center gap-2 px-4 py-1.5 text-xs cursor-pointer transition-colors group ${
            selectedId === ROOT ? 'bg-[#4575A8]/15 text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          }`}
        >
          <Cloud className="w-3.5 h-3.5 flex-shrink-0 text-[#4575A8]" />
          <span className="flex-1 truncate">My Drive</span>
          <button
            data-testid="folder-finder-newroot"
            onClick={(e) => { e.stopPropagation(); onStartCreate(ROOT); }}
            className="opacity-0 group-hover:opacity-100 transition-opacity text-[#4575A8] hover:text-[#5C8AC2]"
            title="New folder at root"
          >
            <FolderPlus className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      {creatingUnder === ROOT && (
        <CreateInput value={createValue} setValue={setCreateValue} onSubmit={() => onSubmitCreate(ROOT)} onCancel={onCancelCreate} depth={1} />
      )}

      {visibleRows.map(({ folder, depth, expanded }) => {
        const isActive = selectedId === folder.id;
        return (
          <div key={folder.id}>
            <div
              data-testid={`folder-finder-row-${folder.id}`}
              onClick={() => onSelect(folder.id, folder.name)}
              style={{ paddingLeft: `${1 + depth * 0.875}rem` }}
              className={`group flex items-center gap-1.5 pr-3 py-1.5 text-xs cursor-pointer transition-colors ${
                isActive ? 'bg-[#4575A8]/15 text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              <button
                onClick={(e) => { e.stopPropagation(); onToggleExpand(folder.id); }}
                className="flex-shrink-0 w-4 h-4 flex items-center justify-center text-muted-foreground hover:text-foreground"
              >
                {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              </button>
              {/* Shared Drives render with a Building icon at depth 0 to
                  visually distinguish them from regular folders inside. */}
              {folder.is_shared_drive && depth === 0 ? (
                <Building className="w-3.5 h-3.5 flex-shrink-0 text-[#C4A561]" />
              ) : (
                <Folder className="w-3.5 h-3.5 flex-shrink-0 text-[#4575A8]" />
              )}
              <span className="flex-1 truncate">{folder.name}</span>
              <button
                data-testid={`folder-finder-newsub-${folder.id}`}
                onClick={(e) => { e.stopPropagation(); onStartCreate(folder.id); }}
                className="opacity-0 group-hover:opacity-100 transition-opacity text-[#4575A8] hover:text-[#5C8AC2]"
                title="New subfolder"
              >
                <FolderPlus className="w-3.5 h-3.5" />
              </button>
            </div>
            {creatingUnder === folder.id && (
              <CreateInput value={createValue} setValue={setCreateValue} onSubmit={() => onSubmitCreate(folder.id)} onCancel={onCancelCreate} depth={depth + 1} />
            )}
          </div>
        );
      })}

      {rootIds.length === 0 && !creatingUnder && (
        <div className="px-4 py-6 text-xs text-muted-foreground text-center">
          No folders accessible to this app yet. Create one with the + icon next to "My Drive" above.
        </div>
      )}
    </div>
  );
}

function CreateInput({ value, setValue, onSubmit, onCancel, depth }) {
  return (
    <div
      style={{ paddingLeft: `${1.5 + depth * 0.875}rem` }}
      className="py-1.5 pr-3 flex items-center gap-2"
    >
      <FolderPlus className="w-3.5 h-3.5 text-[#4575A8] flex-shrink-0" />
      <Input
        autoFocus
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); onSubmit(); }
          else if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
        }}
        placeholder="New folder name…"
        data-testid="folder-finder-create-input"
        className="bg-muted border-border text-foreground h-7 text-xs"
      />
      <Button data-testid="folder-finder-create-submit" size="sm" onClick={onSubmit} disabled={!value.trim()} className="h-7 px-2 bg-[#4575A8] text-white hover:bg-[#5C8AC2]">
        Create
      </Button>
    </div>
  );
}

function RecentList({ loading, items, selectedId, onSelect }) {
  if (loading) {
    return (
      <div className="px-4 py-6 text-xs text-muted-foreground flex items-center gap-2">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading recent folders…
      </div>
    );
  }
  if (!items.length) {
    return (
      <div className="px-4 py-6 text-xs text-muted-foreground text-center">
        No recent folders yet. Pick a folder once and it'll show up here.
      </div>
    );
  }
  return (
    <div className="py-1" data-testid="folder-finder-recent-list">
      {items.map(it => (
        <button
          key={it.folder_id}
          data-testid={`folder-finder-recent-row-${it.folder_id}`}
          onClick={() => onSelect(it.folder_id, it.folder_name)}
          className={`flex items-center gap-2 w-full px-4 py-2 text-left transition-colors text-xs ${
            selectedId === it.folder_id ? 'bg-[#4575A8]/15 text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          }`}
        >
          <Clock className="w-3.5 h-3.5 flex-shrink-0 text-[#4575A8]" />
          <div className="flex-1 min-w-0">
            <div className="truncate text-foreground">{it.folder_name}</div>
            <div className="text-[10px] text-muted-foreground truncate">{it.folder_path || '—'}</div>
          </div>
        </button>
      ))}
    </div>
  );
}

function SelectedFolderPreview({
  folderId, folderName, details, pathBreadcrumbs, loading,
  folderLinks = [], linkContext, linkBusy, onLinkToContext, onUnlink,
}) {
  const isAlreadyLinkedToContext = !!(
    linkContext &&
    folderLinks.find(l =>
      l.entity_type === linkContext.entity_type &&
      l.entity_id === linkContext.entity_id,
    )
  );
  return (
    <div data-testid="folder-finder-preview-pane">
      <div className="flex items-center gap-2 mb-3">
        <Folder className="w-4 h-4 flex-shrink-0 text-[#4575A8]" />
        <span className="text-sm font-medium text-foreground truncate">{folderName || '—'}</span>
      </div>

      <Field label="Path">
        {loading ? (
          <Loader2 className="w-3 h-3 animate-spin text-muted-foreground inline" />
        ) : pathBreadcrumbs?.length ? (
          <div className="flex flex-wrap items-center gap-1 text-[10px] text-muted-foreground">
            {pathBreadcrumbs.map((b, i) => (
              <span key={`${b.id || 'root'}-${i}`} className="flex items-center gap-1">
                {i > 0 && <ChevronRight className="w-2.5 h-2.5 text-muted-foreground inline" />}
                <span className={i === pathBreadcrumbs.length - 1 ? 'text-foreground' : ''}>{b.name}</span>
              </span>
            ))}
          </div>
        ) : (
          <span className="text-[10px] text-muted-foreground">—</span>
        )}
      </Field>

      <Field label="Owner">
        <span className="text-[10px] text-muted-foreground">{details?.owner || '—'}</span>
      </Field>

      <Field label="Last modified">
        <span className="text-[10px] text-muted-foreground">
          {details?.modified ? new Date(details.modified).toLocaleString(undefined, { timeZone: getActiveCompanyTz() }) : '—'}
        </span>
      </Field>

      <Field label="Folder ID">
        <span className="text-[10px] text-muted-foreground font-mono break-all">{folderId === ROOT ? 'root' : folderId}</span>
      </Field>

      {details?.web_view_link && (
        <a
          data-testid="folder-finder-open-in-drive"
          href={details.web_view_link}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#4575A8] hover:underline"
        >
          <ExternalLink className="w-3 h-3" /> Open in Drive
        </a>
      )}

      {/* Generalized linking — shows existing links and a "Link to ..."
          button when `linkContext` is provided by the caller. */}
      {(folderLinks.length > 0 || linkContext) && (
        <div className="mt-4 pt-3 border-t border-border" data-testid="folder-finder-links">
          <div className="text-[9px] uppercase tracking-widest text-muted-foreground mb-2">Linked to</div>
          {folderLinks.length === 0 && (
            <div className="text-[10px] text-muted-foreground">No records linked yet.</div>
          )}
          {folderLinks.map(l => (
            <div key={l.id} className="flex items-center gap-1.5 py-0.5 group">
              <span className="text-[9px] uppercase text-[#4575A8] bg-[#4575A8]/10 px-1.5 py-0.5 rounded">
                {(l.entity_type || 'job').replace(/_/g, ' ')}
              </span>
              <span className="text-[10px] text-muted-foreground truncate flex-1">{l.entity_id}</span>
              <button
                data-testid={`folder-finder-unlink-${l.id}`}
                onClick={() => onUnlink(l.id)}
                disabled={linkBusy}
                className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-[#AD5D5D]"
                title="Remove link"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          {linkContext && !isAlreadyLinkedToContext && (
            <Button
              data-testid="folder-finder-link-to-context"
              size="sm"
              variant="outline"
              disabled={linkBusy}
              onClick={onLinkToContext}
              className="mt-2 w-full border-[#4575A8]/40 text-[#4575A8] hover:text-foreground hover:bg-[#4575A8]/10 h-7 text-[10px]"
            >
              <Link2 className="w-3 h-3 mr-1" /> Link to {linkContext.entity_label || linkContext.entity_type}
            </Button>
          )}
          {linkContext && isAlreadyLinkedToContext && (
            <div className="mt-2 text-[10px] text-[#4575A8]">Already linked.</div>
          )}
        </div>
      )}
    </div>
  );
}

function FlatFolderList({ loading, items, selectedId, onSelect, emptyText, testIdPrefix, showSharer = false }) {
  if (loading) {
    return (
      <div className="px-4 py-6 text-xs text-muted-foreground flex items-center gap-2">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
      </div>
    );
  }
  if (!items.length) {
    return <div className="px-4 py-6 text-xs text-muted-foreground text-center">{emptyText}</div>;
  }
  return (
    <div className="py-1">
      {items.map(f => (
        <button
          key={f.id}
          data-testid={`${testIdPrefix}-${f.id}`}
          onClick={() => onSelect(f.id, f.name)}
          className={`flex items-center gap-2 w-full px-4 py-2 text-left transition-colors text-xs ${
            selectedId === f.id ? 'bg-[#4575A8]/15 text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          }`}
        >
          <Folder className="w-3.5 h-3.5 flex-shrink-0 text-[#4575A8]" />
          <div className="flex-1 min-w-0">
            <div className="truncate text-foreground">{f.name}</div>
            <div className="text-[10px] text-muted-foreground truncate">
              {showSharer && f.shared_by ? `Shared by ${f.shared_by}` : (f.owner || '—')}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function SharedDrivesList({ loading, drives, selectedId, onSelect }) {
  if (loading) {
    return (
      <div className="px-4 py-6 text-xs text-muted-foreground flex items-center gap-2">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
      </div>
    );
  }
  if (!drives.length) {
    return (
      <div className="px-4 py-6 text-xs text-muted-foreground text-center">
        No Shared Drives. Shared Drives are a Google Workspace feature — your account may not have any.
      </div>
    );
  }
  return (
    <div className="py-1">
      {drives.map(d => (
        <button
          key={d.id}
          data-testid={`folder-finder-shared-drive-${d.id}`}
          onClick={() => onSelect(d.id, d.name)}
          className={`flex items-center gap-2 w-full px-4 py-2 text-left transition-colors text-xs ${
            selectedId === d.id ? 'bg-[#4575A8]/15 text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          }`}
        >
          <Building className="w-3.5 h-3.5 flex-shrink-0 text-[#C4A561]" />
          <div className="flex-1 min-w-0">
            <div className="truncate text-foreground">{d.name}</div>
            <div className="text-[10px] text-muted-foreground">Shared Drive</div>
          </div>
        </button>
      ))}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-3">
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1">{label}</div>
      {children}
    </div>
  );
}
